import { Box, Button, Dialog, IconButton, Typography, styled } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type React from 'react';

const StyledTypography = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontSize: '0.875rem',
  lineHeight: 1.6,
  [theme.breakpoints.up('sm')]: {
    fontSize: '1rem',
  },
}));

interface DisclaimerModalProps {
  open: boolean;
  onClose: () => void;
}

const DisclaimerModal: React.FC<DisclaimerModalProps> = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          border: '1px solid #e0e0e0',
          borderRadius: 2,
        }
      }}
    >
      <Box sx={{ position: 'relative', p: 4, backgroundColor: '#fafafa' }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'grey.500',
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          variant="h4"
          component="h2"
          gutterBottom
          sx={{
            fontWeight: 600,
            mb: 3,
            pr: 4,
          }}
        >
          Disclaimer & Important Information
        </Typography>

        <Box sx={{ maxHeight: 'calc(90vh - 180px)', overflowY: 'auto', pr: 2 }}>
          <StyledTypography>
            The AGILE Rates Centre ("the tool") is for general information purposes only. It does not provide advice of a general or personal nature and is not intended to be relied upon for the purposes of making a decision in relation to a financial product. Before acting on anything contained in this material, you should consider the appropriateness of the information received, having regard to your objectives, financial situation and needs. We strongly recommend that you consider obtaining financial advice from a financial adviser before making any financial decision.
          </StyledTypography>

          <StyledTypography>
            AGILE consists of two phases: (1) Growth and (2) Lifetime Income. During the growth phase the investment will continue to benefit from market exposure via returns linked to an equity index that may rise or fall (with a level of investment protection). The Lifetime Income phase can be commenced any time after the first year, by converting the Investment Value to a guaranteed Lifetime Income stream, with a choice of Rising or Fixed Lifetime Income options.
          </StyledTypography>

          <StyledTypography>
            Use of the word 'guarantee' in relation to Allianz Guaranteed Income for Life (AGILE) refers to an assurance that certain conditions or contractual promises will be fulfilled by Allianz Retire+ from the available assets of its Statutory Fund No 2, in relation to the product terms. This includes 'guaranteed' income payments in the Lifetime Income Phase which will be paid from the available assets of Statutory Fund No 2, noting that Allianz Retire+ may terminate the product in certain limited circumstances as outlined in the Product Disclosure Statement available on the Allianz Retire+ website.
          </StyledTypography>

          <StyledTypography>
            AGILE is issued by Allianz Australia Life Insurance Limited (ABN 27 076 033 782) (AFSL 296559) (Allianz Retire+). Allianz Retire+ is a registered business name of Allianz Australia Life Insurance Limited. Prior to making an investment decision you should read the relevant Product Disclosure Statement (PDS) and Target Market Determination (TMD) which are available on our website (<a href='https://www.allianzretireplus.com.au' target='_blank' rel="noopener">https://www.allianzretireplus.com.au</a>). The income rates shown by the tool are effective as at the dates displayed within the tool. The tool is updated regularly with the latest effective rates and is also a repository of historical rates. To the extent permitted by law, neither Allianz Retire+ nor any of its related entities, agents or employees accept any liability for any loss or damage arising, directly or indirectly, from reliance on this tool.
          </StyledTypography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            Important Information about this Tool
          </Typography>

          <StyledTypography>
            Where "you" or "your" is referenced or used in this tool, this should be taken to refer to the 'Life Insured', which (as defined in the PDS) is means an individual natural person nominated on commencement of the investment in respect of whom AGILE benefits are payable. For further details on what Life Insured means and eligibility to invest in AGILE, please refer to the PDS.
          </StyledTypography>

          <StyledTypography>
            Where "Gender" is referenced or used in this tool, this means (as defined in the PDS) the sex recorded at birth of the Life Insured or Surviving Spouse, as Applicable.
          </StyledTypography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            Lifetime Income Rates – Important Information
          </Typography>

          <StyledTypography>
            This tool provides users with applicable AGILE Lifetime Income Rates based on the inputs entered and the AGILE Lifetime Income Rates available at a certain point:
          </StyledTypography>

          <Box sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' }, lineHeight: 1.6 }}>
            <ul style={{ marginTop: 0 }}>
              <li>If you have entered that you are an existing AGILE Investor, the results provided are based on the Lifetime Income Rates effective on your Commencement Date of your initial AGILE investment.</li>
              <li>If you have entered that you are a new AGILE Investor (i.e. have not yet made an AGILE investment), the results provided are based on the Lifetime Income Rates that are currently (or about to be) available to new Investors.</li>
            </ul>
          </Box>

          <StyledTypography>
            Your <strong>Lifetime Income Rate</strong> determines the percentage of Investment Value you will receive when you commence your guaranteed Lifetime Income. It is the sum of an <strong>Age-Based Rate</strong> and a <strong>Total Annual Income Escalator</strong>:
          </StyledTypography>

          <Box sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' }, lineHeight: 1.6 }}>
            <ul style={{ marginTop: 0 }}>
              <li>The <strong>Age-Based Rate</strong> is determined based on your <strong>Gender and Age</strong> at <em>Commencement Date</em> (or where the Spouse Insured Option has been elected, and that Spouse is younger, the Gender and age of your nominated Spouse).</li>
              <li>
                The <strong>Total Annual Income Escalator</strong> means the sum of all the applicable <strong>Annual Income Escalators</strong> for each complete year you wait to start your AGILE Lifetime Income.
                <ul>
                  <li>The Annual Income Escalator means the amount that your Lifetime Income Rate will increase by each year before starting your AGILE Lifetime Income. It is determined based on your Age (or where the Spouse Insured Option has been elected, and that Spouse is younger, the Gender and age of your nominated Spouse)</li>
                </ul>
              </li>
            </ul>
          </Box>

          <StyledTypography>
            Any elections you make relating to the Age Pension+ Option, the Spouse Insured Option and Fixed or Rising Lifetime Income will vary the Age-Based Rates and Annual Income Escalators applicable.
          </StyledTypography>

          <StyledTypography>
            <strong>Please refer to the AGILE PDS for more information on these terms.</strong>
          </StyledTypography>

          <StyledTypography>
            The Lifetime Income Rates for the Age Pension+ Option assume there will be no changes to the Capital Access Schedule between the Commencement Date and the date an investor commences the Age Pension+ Option. Should the Capital Access Schedule change during this time, we reserve the right to update the Lifetime Income Rates offered to an investor in respect of the Age Pension+ Option. For information about how selecting the Age Pension+ option may affect your investment, including your access to your Investment Value on withdrawal or upon death, please refer to the PDS.
          </StyledTypography>

          <StyledTypography>
            * If the Spouse Insured Option is elected, we refer to the younger age and gender of the Life Insured or the Spouse at the Commencement Date in determining the applicable rate. The Spouse Insured Option may be selected when you commence your Lifetime Income. If the Spouse Insured Option is selected, it will enable the Lifetime Income to continue to be paid in the event of the death of the Life Insured, assuming the nominated Spouse is still alive. Please refer to the conditions outlined in the PDS.
          </StyledTypography>

          <StyledTypography>
            We reserve the right to change the Age-Based Rates and Annual Income Escalators offered at any time. This may include limited offers from time to time. Any errors we may identify we will endeavour to rectify as soon as possible.
          </StyledTypography>

          <hr /><br />
          <StyledTypography>
            Contact your Financial Adviser or Allianz Retire+ for further information on applicable Age-Based Rates and Annual Income Escalators.
          </StyledTypography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            Limitations
          </Typography>

          <StyledTypography>
            The results provided in this tool are based entirely on the information provided by the user.
          </StyledTypography>

          <StyledTypography>
            <strong>The final Lifetime Income Rate applicable upon commencing the Lifetime Income Rate is subject to our confirmation and may differ if any of your information or elections made are different to what has been entered in the tool.</strong>
          </StyledTypography>

          <StyledTypography>
            Please note, a lifetime income stream, including AGILE is intended to be a permanent purchase. Some lifetime income streams other than AGILE may not allow withdrawals, or may provide limitations on withdrawals, once commenced. As such, any investor would need to take into account any future lump sum capital consideration they may need (e.g. purchase of new car or medical expenses) and consider how they would fund this when deciding how much to allocate to a lifetime income stream. If a withdrawal is made or the lifetime income stream is closed, the total net benefit the investor receives may be less than if they did not make a withdrawal or close the product.
          </StyledTypography>

          <StyledTypography>
            This tool does not consider personal financial needs, both currently and in the future, including but not limited to your (or your spouse's if applicable) expenditure requirements and debts. When making a financial decision, you should contemplate your needs, objectives, and overall financial situation to determine whether a lifetime income stream, would be appropriate for your circumstances.
          </StyledTypography>

          <StyledTypography>
            This tool is not intended to be relied on for the purpose of making a decision in relation to a financial product. You should consider obtaining advice from a licenced financial adviser before making any financial decisions.
          </StyledTypography>

          <StyledTypography>
            Other limitations include that the results may not reflect:
          </StyledTypography>

          <Box sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' }, lineHeight: 1.6 }}>
            <ul style={{ marginTop: 0 }}>
              <li>If an Investor elects the Age Pension+ Option, AGILE income must be commenced by Life Expectancy – the tool does not apply this limitation and therefore actual experience may differ from what is displayed here.</li>
              <li>Broader impacts of switching on or off the Age Pension+ Option or the Spouse Insured Option, beyond their potential impact on the Lifetime Income Rate shown.</li>
            </ul>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              px: 4,
              py: 1,
              fontWeight: 600,
              textTransform: 'uppercase',
            }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default DisclaimerModal;
